/**
 * The day as a strip: night, sunrise, noon, sunset and the time shown
 * (Sprint 103.0). The editor's sun strip (Sprint 084.3), drawn from the same
 * `dayTimeline` and snapping with the same `timeAtFraction`; only the markup is
 * this page's, because apps/sun may not import apps/web's components.
 */

import type { ReactNode } from 'react';
import { fractionOfTime, timeAtFraction, type LocalTime } from '@archisimple/solar';
import type { Day } from '../model/sky.ts';
import { PlayIcon, useWords } from './common.tsx';

const STEPS = 1440;

export function SunStrip({
  day,
  localTime,
  playing,
  onTime,
  onPlay
}: {
  readonly day: Day;
  readonly localTime: LocalTime;
  readonly playing: boolean;
  readonly onTime: (localTime: LocalTime) => void;
  readonly onPlay: () => void;
}): ReactNode {
  const { t } = useWords();
  const { timeline, solar } = day;
  const pct = (fraction: number): string => `${(fraction * 100).toFixed(2)}%`;
  const labels: { at: number; text: string }[] = [];
  if (timeline.sunrise !== undefined && solar.sunrise.kind === 'at') {
    labels.push({ at: timeline.sunrise, text: solar.sunrise.localTime });
  }
  labels.push({ at: timeline.solarNoon, text: solar.solarNoon.localTime });
  if (timeline.sunset !== undefined && solar.sunset.kind === 'at') {
    labels.push({ at: timeline.sunset, text: solar.sunset.localTime });
  }
  const minute = Math.round(fractionOfTime(localTime) * STEPS);

  return (
    <div className="card card--float strip">
      <button
        type="button"
        className="strip__play"
        aria-label={playing ? t('ar.pause') : t('ar.play')}
        onClick={onPlay}
      >
        <PlayIcon playing={playing} />
      </button>
      <div className="strip__body">
        <div className="strip__track">
          {timeline.nightBands.map(([from, to]) => (
            <span
              key={from}
              className="strip__night"
              style={{ left: pct(from), width: pct(to - from) }}
            />
          ))}
          {timeline.sunrise === undefined ? null : (
            <span className="strip__tick" style={{ left: pct(timeline.sunrise) }} />
          )}
          {timeline.sunset === undefined ? null : (
            <span className="strip__tick" style={{ left: pct(timeline.sunset) }} />
          )}
          <input
            className="strip__range"
            type="range"
            min={0}
            max={STEPS - 1}
            step={1}
            value={minute}
            aria-label={t('ar.time')}
            aria-valuetext={localTime}
            onChange={(event) =>
              onTime(timeAtFraction(Number(event.target.value) / STEPS, timeline.snaps))
            }
          />
          <span className="strip__thumb" style={{ left: pct(minute / STEPS) }} />
        </div>
        <div className="strip__labels" aria-hidden="true">
          {labels.map((label) => (
            <span key={label.text} style={{ left: pct(label.at) }}>
              {label.text}
            </span>
          ))}
        </div>
      </div>
      <span className="strip__time">{localTime}</span>
    </div>
  );
}
